import * as M from './materialize';
import {SignLeaseModal} from "./sign-lease-modal";
import {SweetAlertInput, SweetAlertOptions, SweetAlertResult} from "sweetalert2";
import {SignatureModal} from "./dialogs/signature";
import chroma from 'chroma-js';
import {alea} from 'seedrandom';
import {MaterialAutocomplete} from "./material-autocomplete";
import {BuildingApi} from "./services/building-api";
import {BuildingData} from "./move-in-modal";


interface LegacyPostOptions {
    parameters: [string, string],
    data: any,
    type?: string
}

/*@__PURE__:true*/
export function doPostAsync(url: string, options: LegacyPostOptions) {
    return Utils.post(url, {
        body: options.data,
        parameters: options.parameters,
        type: options.type
    });
}

(window as any).doPostAsync = doPostAsync;

// Base options that might be common
interface RequestOptionsBase {
    parameters?: Record<string, string | number | boolean>;
    /** Headers to include. Can be a Headers object or a simple key-value pair. */
    headers?: Headers | Record<string, string>;
    /** Expected response type. Defaults to 'json'. */
    type?: 'text' | 'json';
}

// Options specific to the upload method
interface UploadOptions<B = any> extends RequestOptionsBase {
    /** The body of the request, typically FormData for file uploads. */
    body: B;
    /** Callback function for upload progress updates. */
    onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void;
}

// Helper to process response text
const processResponseText = async (responseText: string, responseType?: 'text' | 'json'): Promise<any> => {
    if (responseType === 'text') {
        return responseText;
    }
    try {
        return JSON.parse(responseText);
    } catch (e) {
        // Return a consistent error structure if JSON parsing fails
        return {
            error: 'Failed to parse JSON response.',
            responseText: responseText
        };
    }
};

// Guards against multiple concurrent 401s stacking up several re-auth modals.
let reauthInProgress = false;

/**
 * Fetch a fresh CSRF token by loading the (permit-all) /login page and scraping
 * its hidden token. After a session times out the in-page _csrf meta is stale
 * relative to the new session, so we must read a token tied to the live session
 * before we can POST credentials back to Spring's form-login.
 */
async function fetchFreshCsrf(): Promise<{ token: string; param: string }> {
    const res = await fetch('/login', { credentials: 'same-origin' });
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const hidden = doc.querySelector<HTMLInputElement>('input[type="hidden"][value]');
    const meta = doc.querySelector('meta[name="_csrf"]');
    return {
        token: hidden?.value || meta?.getAttribute('content') || '',
        param: hidden?.name || '_csrf',
    };
}

/**
 * Re-authenticate the user *in place* instead of bouncing them to /login.
 *
 * The old behaviour redirected to /login on any 401/403; because the 401 came
 * from an XHR, Spring's SavedRequest held the API URL (not the page the user was
 * looking at), so the post-login "continue" was fragile — it dumped users on a
 * JSON endpoint or force-redirected to /dashboard. Here we keep them on the
 * current page, take their credentials in a modal, refresh the session cookie,
 * then reload the *same* URL so they land exactly where they were with a fresh
 * CSRF token in the DOM.
 */
function handleSessionExpired() {
    if (reauthInProgress) return;
    reauthInProgress = true;

    import('sweetalert2').then((mod) => {
        const Swal = mod.default;
        const prefillUser = document.querySelector('meta[name="_user"]')?.getAttribute('content') || '';

        Swal.fire({
            title: 'Session expired',
            html: `
                <p style="margin:0 0 16px;color:#6b7280;font-size:0.92rem;">
                    You were signed out due to inactivity. Sign back in to pick up right where you left off.
                </p>
                <input id="reauth-username" class="swal2-input" placeholder="Username"
                       autocomplete="username" value="${prefillUser.replace(/"/g, '&quot;')}">
                <input id="reauth-password" type="password" class="swal2-input" placeholder="Password"
                       autocomplete="current-password">
            `,
            icon: 'warning',
            focusConfirm: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showDenyButton: true,
            confirmButtonText: 'Sign in',
            denyButtonText: 'Go to login page',
            didOpen: () => {
                const user = document.getElementById('reauth-username') as HTMLInputElement;
                const pass = document.getElementById('reauth-password') as HTMLInputElement;
                (prefillUser ? pass : user)?.focus();
                // Enter on the password field submits the modal.
                pass?.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') Swal.clickConfirm();
                });
            },
            preConfirm: async () => {
                const username = (document.getElementById('reauth-username') as HTMLInputElement).value.trim();
                const password = (document.getElementById('reauth-password') as HTMLInputElement).value;
                if (!username || !password) {
                    Swal.showValidationMessage('Please enter your username and password.');
                    return false;
                }
                try {
                    const { token, param } = await fetchFreshCsrf();
                    const body = new URLSearchParams();
                    body.set('username', username);
                    body.set('password', password);
                    body.set('remember-me', 'on');
                    if (token) body.set(param, token);

                    const res = await fetch('/login', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: body.toString(),
                    });

                    // Spring's failure handler redirects to /login?error=...; success
                    // lands anywhere else. response.url reflects the followed redirect.
                    if (res.url.includes('/login')) {
                        Swal.showValidationMessage('Incorrect username or password.');
                        return false;
                    }
                    return true;
                } catch (e) {
                    Swal.showValidationMessage('Could not reach the server. Please try again.');
                    return false;
                }
            },
        }).then((result) => {
            reauthInProgress = false;
            if (result.isConfirmed) {
                // Reload the current page: same URL (back where they were) + fresh CSRF.
                window.location.reload();
            } else if (result.isDenied) {
                window.location.href = '/login';
            }
        });
    }).catch(() => {
        reauthInProgress = false;
        window.location.href = '/login';
    });
}

/*@__PURE__:true*/
export class Utils {
    private static CACHE_TTL = 5 * 60 * 1000;

    static invalidateCache(endpoint: string) {
        console.warn(`Invalidating cache for: ${endpoint}`);
        // Invalidate the specific endpoint
        localStorage.removeItem(endpoint);

        // Also invalidate the "parent" collection endpoint
        // e.g., a change to '/api/students/123' should invalidate '/api/students'
        const lastSlash = endpoint.lastIndexOf('/');
        if (lastSlash > 0) {
            const parentEndpoint = endpoint.substring(0, lastSlash);
            console.warn(`Invalidating parent cache: ${parentEndpoint}`);
            localStorage.removeItem(parentEndpoint);
        }
    }

    static mergeArraysById<T extends { id: number }>(arr1: T[], arr2: T[]): T[] {
        const map = new Map<number, T>();
        arr1.forEach(item => map.set(item.id, item));
        arr2.forEach(item => {
            if (!map.has(item.id)) {
                map.set(item.id, item);
            }
        });
        return Array.from(map.values());
    }

    static isEmpty(value: any): boolean {
        return value === null || value === undefined || value === '';
    }

    static isNumber(value: any): boolean {
        return !isNaN(parseFloat(value)) && isFinite(value);
    }

    static packer(o: any) {
        if (o === null || o === undefined || o.length == 0) return '';
        const str = JSON.stringify(o);
        const mess = btoa(str);
        return mess.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ',');
    }

    static startLoading<T extends HTMLElement>(element?: T) {
        if (!element) return;
        element.setAttribute('disabled', 'true');
        element.setAttribute('data-old-text', element.innerHTML);
        element.innerHTML = '<i class="material-icons spinner">hourglass_empty</i>';
    }

    static stopLoading<T extends HTMLElement>(element?: T) {
        if (!element) return;
        element.removeAttribute('disabled');
        element.innerHTML = element.getAttribute('data-old-text') ? element.getAttribute('data-old-text')! : '<i class="material-icons left">done</i>';
        element.querySelector('.waves-ripple')?.remove();
        element.removeAttribute('data-old-text');
    }

    static formatDate(date: Date, format: string): string {
        const map: { [key: string]: string } = {
            'MM': ('0' + (date.getMonth() + 1)).slice(-2),
            'DD': ('0' + date.getDate()).slice(-2),
            'YYYY': date.getFullYear().toString(),
        };

        return format.replace(/MM|DD|YYYY/gi, matched => map[matched]);
    }

    static get loadingIcon(): HTMLElement {
        const spinner = document.createElement('div');
        spinner.classList.add('preloader-wrapper', 'small', 'active');

        const spinnerLayer = document.createElement('div');
        spinnerLayer.classList.add('spinner-layer', 'spinner-blue-only');

        const circleClipperLeft = document.createElement('div');
        circleClipperLeft.classList.add('circle-clipper', 'left');
        const circleLeft = document.createElement('div');
        circleLeft.classList.add('circle');
        circleClipperLeft.appendChild(circleLeft);

        const gapPatch = document.createElement('div');
        gapPatch.classList.add('gap-patch');
        const circleGap = document.createElement('div');
        circleGap.classList.add('circle');
        gapPatch.appendChild(circleGap);

        const circleClipperRight = document.createElement('div');
        circleClipperRight.classList.add('circle-clipper', 'right');
        const circleRight = document.createElement('div');
        circleRight.classList.add('circle');
        circleClipperRight.appendChild(circleRight);

        spinnerLayer.appendChild(circleClipperLeft);
        spinnerLayer.appendChild(gapPatch);
        spinnerLayer.appendChild(circleClipperRight);
        spinner.appendChild(spinnerLayer);

        return spinner;
    }

    /**
     * Displays an error toast message using Materialize CSS.
     * This function now accounts for the standardized error response from the backend.
     *
     * @param title The main title for the error toast.
     * @param error The error object, which can be a string, a generic Error, or the structured backend error.
     */
    static showErrorToast(title: string, error: any) {
        let messageHtml = `<div class="toast-title">${title}</div>`; // Main title for the toast

        let displayMessage = 'An unknown error occurred.'; // Default message
        let detailsMessage = '';
        let stackTraceMessage = '';
        let readMoreAvailable = false;

        // Check if the error object conforms to the backend's new structured format
        if (error && typeof error === 'object' && error.message) {
            displayMessage = error.message;
            detailsMessage = error.details || '';
            stackTraceMessage = error.stackTrace || '';
            readMoreAvailable = error.readMore === true;

            // Start with the primary error message from the backend
            messageHtml += `<div class="toast-main-message" style="color:yellow; font-weight: bold; margin-top: 5px;">${displayMessage}</div>`;

            if (readMoreAvailable && detailsMessage) {
                // If 'readMore' is true, provide a clickable action to reveal more details
                messageHtml += `
                <div style="margin-top: 10px;">
                    <a class="btn-flat toast-action"
                       onclick="
                           const detailsDiv = this.nextElementSibling;
                           if (detailsDiv.style.display === 'none') {
                               detailsDiv.style.display = 'block';
                               this.textContent = 'Hide Details';
                           } else {
                               detailsDiv.style.display = 'none';
                               this.textContent = 'Read More';
                           }
                       "
                       style="color: rgba(255, 255, 255, 0.9); text-decoration: underline;">Read More</a>
                    <div style="display:none; color: rgba(255, 255, 255, 0.7); font-size: 0.9em; max-height: 150px; overflow-y: auto; padding: 5px 0;">
                        <strong>Details:</strong><br/>
                        ${detailsMessage.replace(/\n/g, '<br/>')}
                        ${stackTraceMessage ? `<br/><br/><strong>Stack Trace:</strong><br/>${stackTraceMessage.replace(/\n/g, '<br/>')}` : ''}
                    </div>
                </div>
            `;
            }
        } else if (error?.message) {
            // Fallback for standard JavaScript Error objects or similar structures
            displayMessage = error.message;
            messageHtml += `<div class="toast-main-message" style="color:yellow; font-weight: bold; margin-top: 5px;">${displayMessage}</div>`;
        } else {
            // Fallback for string errors, or any other unhandled error types
            const genericMessage = (typeof error === 'string') ? error : error?.responseText ?? (error instanceof Error ? error.message : JSON.stringify(error ?? 'Unknown error'));
            displayMessage = genericMessage;
            messageHtml += `<div class="toast-main-message" style="color:yellow; font-weight: bold; margin-top: 5px;">${displayMessage}</div>`;
        }

        // Display the toast with the constructed HTML content
        Utils.showToastMessage(messageHtml, ToastType.ERROR, 10000); // Increased duration to allow time to read/interact
    }

    static showCustomToast(message: string, icon: string, delay = 4000) {
        M.toast({
            html: `<div style="display: flex; align-items: center;">
 <span class="material-icons left white-text" style="margin-right: 12px">${icon}</span> <strong>${message}</strong></div>`,
            classes: 'blue lighten-1 white-text',
            displayLength: delay
        });
    }

    /**
     * Displays a toast message with a specified type.
     *
     * @param {string} message - The message to display in the toast.
     * @param {ToastType} [type=ToastType.INFO] - The type of toast message (e.g., success, error, warning, info).
     */
    static showToastMessage(message: string, type?: ToastType | string, delay = 4000): void {
        let icon = 'done';
        let color = 'green';
        let title = 'Success';

        if (type === null) {
            type = ToastType.INFO;
        }

        switch (type) {
            case ToastType.ERROR:
                icon = 'error';
                color = 'red';
                break;
            case ToastType.WARNING:
                icon = 'warning';
                color = 'orange';
                break;
            case ToastType.INFO:
                icon = 'info';
                color = 'blue';
                break;
        }

        M.toast({
            html: `<div style="display: flex; align-items: center;">
 <span class="material-icons left white-text" style="margin-right: 12px">${icon}</span> <strong>${message}</strong></div>`,
            classes: color + ' lighten-1 white-text',
            displayLength: delay
        });
    }

    static getTextFromLink(htmlString: string | null) {
        if (htmlString === null) return '';
        let regex = /<a[^>]*>([^<]+)<\/a>/i;
        let match = htmlString.match(regex);
        return match ? match[1] : '';
    }

    static getMonthNumber(month: string): number {
        switch (month) {
            case 'Jan':
                return 0;
            case 'Feb':
                return 1;
            case 'Mar':
                return 2;
            case 'Apr':
                return 3;
            case 'May':
                return 4;
            case 'Jun':
                return 5;
            case 'Jul':
                return 6;
            case 'Aug':
                return 7;
            case 'Sep':
                return 8;
            case 'Oct':
                return 9;
            case 'Nov':
                return 10;
            case 'Dec':
                return 11;
            default:
                return -1;
        }
    }

    static getMonthName(month: number): string {
        switch (month) {
            case 0:
                return 'Jan';
            case 1:
                return 'Feb';
            case 2:
                return 'Mar';
            case 3:
                return 'Apr';
            case 4:
                return 'May';
            case 5:
                return 'Jun';
            case 6:
                return 'Jul';
            case 7:
                return 'Aug';
            case 8:
                return 'Sep';
            case 9:
                return 'Oct';
            case 10:
                return 'Nov';
            case 11:
                return 'Dec';
            default:
                return '';
        }
    }

    static dateToString(date: Date | undefined): string | undefined {
        if (!date) return undefined;
        //Aggressively handle date types
        if(typeof date === 'string') {
            date = Utils.stringToDate(date) ?? new Date();
        }else if(typeof date !== 'object' || !(date instanceof Date)) {
            console.warn('Invalid date type:', date);
            return undefined;
        }
        const dateStr = date.getDate() < 10 ? '0' + date.getDate() : date.getDate();
        return `${Utils.getMonthName(date.getMonth())} ${dateStr}, ${date.getFullYear()}`;
    }

    //Format DD MMM, YYYY
    static stringToDate(dateStr: string): Date | null {
        if (dateStr === null || dateStr === undefined) return null;
        const dateParts = dateStr.split(' ');
        if (dateParts.length !== 3) return null;
        const day = parseInt(dateParts[1]);
        const month = Utils.getMonthNumber(dateParts[0]);
        const year = parseInt(dateParts[2]);
        return new Date(year, month, day);
    }

    static stringToDateISO(dateStr: string): Date | null {
        // Expecting format YYYY-MM-DD
        if (dateStr === null || dateStr === undefined) return null;
        const dateParts = dateStr.split('-');
        if (dateParts.length !== 3) return null;
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Months are 0-based
        const day = parseInt(dateParts[2]);
        return new Date(year, month, day);
    }

    static stringToDateTime(dateStr: string): Date | null {
        if (dateStr === null || dateStr === undefined) return null;
        const dateParts = dateStr.split(' ');
        if (dateParts.length !== 4) return null;
        const day = parseInt(dateParts[1]);
        const month = Utils.getMonthNumber(dateParts[0]);
        const year = parseInt(dateParts[2]);
        const hour = parseInt(dateParts[3].split(':')[0]);
        const minute = parseInt(dateParts[3].split(':')[1]);
        return new Date(year, month, day, hour, minute);
    }

    static delete(url: string, Options: PostOptions = {}): Promise<any> {
        let token = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
        let header = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

        const headers = new Headers();
        headers.append(header as string, token as string);
        headers.append('Content-Type', 'application/json');

        if (Options.parameters) {
            url += '?';
            for (const key in Options.parameters) {
                url += key + '=' + Options.parameters[key] + '&';
            }
        }

        return fetch(url, {
            method: 'DELETE',
            headers: headers,
            body: Options.body === null ? '' : JSON.stringify(Options.body)
        }).then(async (response) => {
            if (response.status === 401 || response.status === 403) {
                handleSessionExpired();
                // Reject the promise so the original caller's .catch doesn't run
                return Promise.reject({ responseText: 'Session expired', isSessionError: true });
            }
            if (response.ok) {
                this.invalidateCache(url);
                const text = await response.text();
                try {
                    return JSON.parse(text);
                } catch (e) {
                    return text;
                }
            } else {
                const text = await response.text();
                return Promise.reject({
                    responseText: text
                });
            }
        }).catch(error => {
            if (error && error.isSessionError) {
                return;
            }
            console.error(error.stack || error);
            try {
                if (error instanceof Response) {
                    error.text().then(text => {
                        return Promise.reject({
                            responseText: text
                        });
                    });
                } else {
                    let errorObj = 'Unable to process request at this time.';
                    if(typeof error?.responseText === 'string'){
                        errorObj = JSON.parse(error?.responseText);
                    }
                    return Promise.reject(errorObj);
                }
            } catch (ex) {
                return Promise.reject({
                    responseText: error?.responseText ?? 'An error occurred while processing your request. Please try again later.'
                });
            }
        });
    }

    static fetch<T>(url: string, options: FetchOptions = {}): Promise<any> {
        let token = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
        let header = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

        const headers = new Headers(options.headers);
        headers.append(header as string, token as string);
        headers.append('Content-Type', 'application/json');

        if (options.parameters) {
            url += '?';
            for (const key in options.parameters) {
                url += key + '=' + options.parameters[key] + '&';
            }
        }

        return new Promise((resolve, reject) => {
            fetch(url, {
                ...options,
                headers: headers,
                cache: options.cache || 'no-cache',
                signal: options.signal
            }).then(async response => {
                // Same trap as in post(): a 302 to /login or /access-denied is followed by fetch and
                // 200, so !response.ok never catches it and the login page gets parsed as data.
                if (Utils.isAuthBounce(response)) {
                    const error = new Error('Session expired');
                    (error as any).status = 401;
                    (error as any).isSessionError = true;
                    handleSessionExpired();
                    throw error;
                }
                if (!response.ok) {
                    const error = new Error(`HTTP error! status: ${response.status}`);
                    (error as any).status = response.status;
                    try {//Try and read the response text
                        (error as any).responseText = await response.text();
                    } catch (e) {
                        //Ignore
                    }
                    throw error;
                }

                if (options.type === 'file') {
                    const blob = await response.blob();
                    // Strip the surrounding quotes / trailing params off `filename="x.xlsx"`,
                    // otherwise the quotes leak into the saved name (which made the OS flag the
                    // download as an unknown/executable file).
                    const filename = response.headers.get('Content-Disposition')
                        ?.split('filename=')[1]?.split(';')[0]?.replace(/["']/g, '').trim();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = decodeURI(filename ?? 'file');
                    document.body.appendChild(a);
                    a.click();
                    resolve(window.URL.revokeObjectURL(url));
                    a.remove();
                    // Return, like the SSE branch does. Without this the code below tried to read
                    // the same body again as text and threw "body stream already read" — harmless
                    // (the promise had already resolved and the file had already saved) but it put a
                    // red "Fetch error" in the console on every single download in the app.
                    return;
                }

                if(options.type === 'SSE') {
                    resolve(response);
                    return;
                }

                const text = await response.text();
                if (options.type === 'text') {
                    resolve(text);
                    return text;
                }
                try {
                    const json: T = JSON.parse(text);
                    // If not JSON, resolve with text
                    resolve(json);
                    return json;
                } catch (e) {
                    resolve(text);

                    return text;
                }
            }).catch(error => {
                console.error('Fetch error:', error);
                reject(error);
            });
        });
    }

    static debounce<T extends (...args: any[]) => any>(
        func: T,
        wait: number
    ): (...args: Parameters<T>) => ReturnType<T> | void {
        let timeout: ReturnType<typeof setTimeout> | null = null;
        let lastArgs: Parameters<T> | null = null;
        let lastContext: ThisParameterType<T> | null = null;

        const later = () => {
            timeout = null;
            if (lastArgs !== null) {
                func.apply(lastContext, lastArgs);
                lastArgs = null;
                lastContext = null;
            }
        };

        return function (this: ThisParameterType<T>, ...args: Parameters<T>): ReturnType<T> | void {
            lastContext = this;
            lastArgs = args;

            const callNow = !timeout;

            if (timeout) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(later, wait);

            if (callNow) {
                // Clear stored arguments to prevent trailing call
                lastArgs = null;
                lastContext = null;
                return func.apply(this, args);
            }
        };
    }

// Helper function to set data in localStorage
    static setInStorage(name: string, value: any) {
        const item = {
            data: value,
            timestamp: new Date().getTime(),
        };
        localStorage.setItem(name, JSON.stringify(item));
    }

    static async getWithCache<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
        const cachedData = this.getFromStorage(endpoint);
        if (cachedData) {
            console.log(`[CACHE HIT] Serving from localStorage for: ${endpoint}`);
            return Promise.resolve(cachedData as T);
        }

        console.log(`[CACHE MISS] Fetching from network for: ${endpoint}`);
        const response = await this.fetch<T>(endpoint, options);
        this.setInStorage(endpoint, response);
        return response;
    }

//Helper function to delete data from localStorage
    static deleteFromStorage(name: string) {
        const old = localStorage.getItem(name);
        if (old) {
            console.info('Deleted from storage:', name);
            localStorage.removeItem(name);
        } else {
            console.warn('Attempted to delete non-existent item:', name);
        }
    }

    static getFromStorage(name: string, json = true): any | null {
        const itemStr = localStorage.getItem(name);
        if (!itemStr) {
            return null;
        }

        // Logic for new TTL-based JSON cache
        if (json) {
            try {
                const item = JSON.parse(itemStr);
                // Check if the item is a valid TTL object
                if (item && typeof item === 'object' && item.hasOwnProperty('timestamp') && item.hasOwnProperty('data')) {
                    const now = new Date().getTime();

                    // Check if cache has expired
                    if (now > item.timestamp + this.CACHE_TTL) {
                        localStorage.removeItem(name); // Clean up expired item
                        return null;
                    }

                    return item.data;
                } else {
                    // It's JSON, but not our TTL format. Treat as invalid for this cache type.
                    return item;
                }
            } catch (e) {
                // If parsing fails, it's not valid JSON. Return null for a JSON request.
                return null;
            }
        }
        // Logic for simple, non-JSON string values
        else {
            return itemStr;
        }
    }

    static navigate(s: string) {
        window.open(s, '_blank');
    }


    static monthDiff(moveInDate: Date | string, moveOutDate: Date | string) {
        if (typeof moveInDate === 'string') {
            moveInDate = Utils.stringToDate(moveInDate) ?? new Date();
        }
        if (typeof moveOutDate === 'string') {
            moveOutDate = Utils.stringToDate(moveOutDate) ?? new Date();
        }
        return moveOutDate.getMonth() - moveInDate.getMonth() + (12 * (moveOutDate.getFullYear() - moveInDate.getFullYear())) + 1;
    }

    static put<T>(endpoint: string, opt: PostOptions): Promise<T> {
        let token = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
        let header = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

        if (opt.parameters) {
            endpoint += '?';
            for (const key in opt.parameters) {
                endpoint += key + '=' + opt.parameters[key] + '&';
            }
        }

        const headers = new Headers();
        headers.append(header as string, token as string);
        headers.append('Content-Type', 'application/json');
        //Merge option headers with existing, replacing existing values
        if (opt.headers) {
            opt.headers.forEach((value, key) => {
                headers.set(key, value);
            });
        }

        const processResponse = async (response: Response): Promise<any> => {
            const text = await response.text();
            if (opt.type === 'text') return text;
            try {
                return JSON.parse(text);
            } catch (e) {
                return {
                    responseText: text
                };
            }
        }

        let bodyData = opt.body;
        //Check if not FormData using headers
        if (!(opt.body instanceof FormData) && headers.get('Content-Type') === 'application/json') {
            bodyData = JSON.stringify(opt.body);
        } else if (opt.body instanceof FormData) {
            headers.delete('Content-Type');
        }

        return new Promise((resolve, reject) => {
            fetch(endpoint, {
                method: 'PUT',
                headers: headers,
                body: opt.body === null ? '' : bodyData
            }).then(response => {
                if (response.status === 401 || response.status === 403) {
                    handleSessionExpired();
                    // Reject the promise so the original caller's .catch doesn't run
                    return reject({ responseText: 'Session expired', isSessionError: true });
                }
                if (response.ok) {
                    this.invalidateCache(endpoint);
                    processResponse(response).then(json => {
                        Utils.setInStorage(endpoint, json);
                        resolve(json);
                    });
                } else {
                    processResponse(response).then(json => {
                        reject(json);
                    });
                }
            }).catch(error => {
                if (error && error.isSessionError) {
                    return;
                }
                console.error(error.stack || error);
                try {
                    if (error instanceof Response) {
                        error.text().then(text => {
                            reject({
                                responseText: text
                            });
                        });
                    } else {
                        reject({
                            responseText: 'An error occurred while processing your request. Please try again later.'
                        });
                    }
                } catch (ex) {
                    reject({
                        responseText: 'An error occurred while processing your request. Please try again later.'
                    });
                }
            });
        });
    }

    static upload<T>(endpoint: string, opt: UploadOptions): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            let fullEndpoint = endpoint;
            if (opt.parameters) {
                const params = new URLSearchParams();
                for (const key in opt.parameters) {
                    params.append(key, String(opt.parameters[key]));
                }
                fullEndpoint += '?' + params.toString();
            }

            xhr.open('POST', fullEndpoint, true);

            // 2. Set CSRF headers
            const csrfToken = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
            const csrfHeaderName = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

            if (csrfHeaderName && csrfToken) {
                xhr.setRequestHeader(csrfHeaderName, csrfToken);
            }

            let bodyData = opt.body;
            const isFormData = opt.body instanceof FormData;
            let contentTypeFromOptions: string | null = null;

            if (opt.headers) {
                if (opt.headers instanceof Headers) { // Fetch API Headers object
                    opt.headers.forEach((value, key) => {
                        if (key.toLowerCase() === 'content-type') {
                            contentTypeFromOptions = value;
                        }
                        xhr.setRequestHeader(key, value);
                    });
                } else { // Record<string, string>
                    for (const key in opt.headers) {
                        if (key.toLowerCase() === 'content-type') {
                            contentTypeFromOptions = opt.headers[key];
                        }
                        xhr.setRequestHeader(key, opt.headers[key]);
                    }
                }
            }

            if (isFormData) {
                //Let the browser set the Content-Type for FormData
            } else {
                // If not FormData, and no Content-Type explicitly set or it's application/json
                if (!contentTypeFromOptions || contentTypeFromOptions.toLowerCase() === 'application/json') {
                    if (!contentTypeFromOptions) { // only set if not already set by opt.headers
                        xhr.setRequestHeader('Content-Type', 'application/json');
                    }
                    bodyData = JSON.stringify(opt.body);
                }
                // If contentTypeFromOptions was something else (e.g., 'text/plain'), it's already set.
            }

            if (opt.onProgress && xhr.upload) {
                xhr.upload.onprogress = (event: ProgressEvent) => {
                    if (event.lengthComputable) {
                        opt.onProgress!({
                            loaded: event.loaded,
                            total: event.total,
                            percentage: Math.round((event.loaded / event.total) * 100)
                        });
                    }
                };
            }

            xhr.onload = async () => {
                try {
                    const processedResponse = await processResponseText(xhr.responseText, opt.type);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(processedResponse as T);
                    } else {
                        reject(processedResponse); // Contains original responseText if JSON parsing failed
                    }
                } catch (error) { // Catch errors from processResponseText or other issues
                    reject({
                        responseText: xhr.responseText,
                        error: 'Error processing response: ' + (error as Error).message
                    });
                }
            };

            xhr.onerror = () => {
                reject({
                    responseText: 'Network error occurred.',
                    status: xhr.status,
                    statusText: xhr.statusText
                });
            };

            xhr.onabort = () => {
                reject({
                    responseText: 'Upload aborted by user.',
                    status: 0 // Or appropriate status for abort
                });
            };

            xhr.send(bodyData as any);
        });
    }

    /**
     * Stamp a rejection with the HTTP status the server answered with.
     *
     * <p>Without this a caller cannot tell <i>"the server refused this"</i> from <i>"this never
     * reached a server"</i>: a plain-text error body and a dead network both arrive as a bare
     * {@code responseText}. That ambiguity had the room-inspection worklist queue a 400 as though
     * the phone were offline, telling the inspector their work would sync itself when it never
     * could.</p>
     *
     * <p>Purely additive — an existing status on the payload is never overwritten, and callers
     * reading {@code message} or {@code responseText} are unaffected.</p>
     */
    private static withStatus(error: any, status: number): any {
        if (error && typeof error === 'object') {
            if (error.status === undefined) error.status = status;
            return error;
        }
        return {responseText: typeof error === 'string' ? error : String(error ?? ''), status};
    }

    /**
     * Did this response come back from an authentication page rather than the endpoint we asked
     * for?
     *
     * <p><b>Both destinations count.</b> Spring sends an <em>anonymous</em> caller to {@code /login}
     * via the AuthenticationEntryPoint, but an <em>authenticated</em> one without the authority to
     * {@code /access-denied} via the AccessDeniedHandler — and a stale session on a public form can
     * land on either. Measured chain for an anonymous POST to a guarded path:
     * {@code /settings → /access-denied → /login}. Checking only {@code /login} missed the case
     * where the chain stops one hop earlier.</p>
     *
     * <p>Only ever true when the request was actually redirected, so a page that answers on those
     * paths directly is left alone.</p>
     */
    static isAuthBounce(response: Response): boolean {
        if (!response.redirected || !response.url) return false;
        try {
            const path = new URL(response.url, window.location.origin).pathname;
            return path === '/login' || path.startsWith('/login/') || path === '/access-denied';
        } catch {
            return false;
        }
    }

    static post<T>(endpoint: string, opt: PostOptions): Promise<T> {
        let token = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
        let header = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

        if (opt.parameters) {
            endpoint += '?';
            for (const key in opt.parameters) {
                endpoint += key + '=' + opt.parameters[key] + '&';
            }
        }

        const headers = new Headers();
        headers.append(header as string, token as string);
        headers.append('Content-Type', 'application/json');
        //Merge option headers with existing, replacing existing values
        if (opt.headers) {
            opt.headers.forEach((value, key) => {
                headers.set(key, value);
            });
        }

        const processResponse = async (response: Response): Promise<any> => {
            const text = await response.text();
            if (opt.type === 'text') return text;
            try {
                return JSON.parse(text);
            } catch (e) {
                return {
                    responseText: text
                };
            }
        }

        let bodyData = opt.body;
        //Check if not FormData using headers
        if (!(opt.body instanceof FormData) && headers.get('Content-Type') === 'application/json') {
            bodyData = JSON.stringify(opt.body);
        } else if (opt.body instanceof FormData) {
            headers.delete('Content-Type');
        }

        return new Promise((resolve, reject) => {
            fetch(endpoint, {
                method: 'POST',
                headers: headers,
                body: opt.body === null ? '' : bodyData
            }).then(response => {
            // Check for session-related errors FIRST
            if (response.status === 401 || response.status === 403) {
                handleSessionExpired();
                // Reject the promise so the original caller's .catch doesn't run
                return reject({ responseText: 'Session expired', isSessionError: true });
            }
            // A login bounce is not a 401. Spring answers an unauthenticated request with a 302 to
            // /login, fetch follows it silently, and what arrives is a 200 carrying the login page —
            // so response.ok is true and the check above never fires. The apply form read that as a
            // successful submission and showed a student "Success" over the login page, with
            // nothing written. Same test the service worker already applies to navigations:
            // "a redirect is a login bounce, never the page".
            if (Utils.isAuthBounce(response)) {
                handleSessionExpired();
                return reject({ responseText: 'Session expired', isSessionError: true });
            }
            if (response.ok) {
                this.invalidateCache(endpoint);
                processResponse(response).then(json => {
                    if(!opt.disableCache){
                        Utils.setInStorage(endpoint, json);
                    }
                    resolve(json);
                });
            } else {
                processResponse(response).then(json => {
                    reject(Utils.withStatus(json, response.status));
                });
            }
            }).catch(error => {
                if (error && error.isSessionError) {
                    return;
                }
                console.error(error.stack || error);
                try {
                    if (error instanceof Response) {
                        error.text().then(text => {
                            reject(Utils.withStatus({responseText: text}, error.status));
                        });
                    } else {
                        reject({
                            responseText: 'An error occurred while processing your request. Please try again later.',
                            // The request never produced a response. Callers that queue work for
                            // later need this: without it, a request that never left the device is
                            // indistinguishable from one the server refused, because both arrive as
                            // a bare responseText.
                            networkError: true
                        });
                    }
                } catch (ex) {
                    reject({
                        responseText: 'An error occurred while processing your request. Please try again later.'
                    });
                }
            });
        });
    }

    static toNumber(rate: string | undefined | number) {
        if (typeof rate === 'number') return rate;
        if (!rate) return 0;
        return parseFloat(rate.replace('R', '').replace(',', '').replace(' ', ''));
    }

    static getCSRFToken() {
        let token = document.querySelector("meta[name='_csrf']")?.getAttribute("content");
        let header = document.querySelector("meta[name='_csrf_header']")?.getAttribute("content");

        return {header: header, token: token};
    }

    static setCookie(s: string, true1: string, number: number) {
        document.cookie = s + '=' + true1 + ';max-age=' + number;
    }

    static getCookie(s: string) {
        let cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i].trim();
            if (cookie.indexOf(s) === 0) {
                return cookie.substring(s.length + 1, cookie.length);
            }
        }
        return undefined;
    }

    static generateColorArray(length: number, seed = '4234') {
        const colors: string[] = [];
        const random = alea(seed);

        for (let i = 0; i < length; i++) {
            const color = chroma.random();
            colors.push(color.hex());
        }
        return colors;
    }

    private static generateRandomColor() {
        return '#' + Math.floor(Math.random() * 16777215).toString(16);
    }

    static generateColorFromSeed(seed: string, count: number = 5) {
        const colors: string[] = [];
        const prng = alea(seed); // Seeded PRNG

        for (let i = 0; i < count; i++) {
            // Generate HSL components using seeded random
            const h = prng() * 360;       // Hue (0-360)
            const s = prng() * 0.4 + 0.4; // Saturation (40-80%)
            const l = prng() * 0.4 + 0.4; // Lightness (40-80%)

            // Create Chroma color and convert to HEX
            const color = chroma.hsl(h, s, l, 1).hex();
            colors.push(color);
        }

        return colors;
    }

    /**
     * Custom implementation of Server-Sent Events using fetch to support headers (CSRF) and cache control.
     * Returns a function to abort the connection.
     */
    static fetchEventSource(url: string, options: {
        parameters?: { [key: string]: any },
        onEvent: (eventName: string, data: any) => void,
        onError?: (error: any) => void
    }): () => void {
        const controller = new AbortController();
        const signal = controller.signal;



        this.fetch(url, {
            parameters: options.parameters,
            cache: 'no-cache',
            signal: signal,
            type: 'SSE',
        }).then(async value => {
            const reader = value.body?.getReader();
            if (!reader) {
                throw new Error('ReadableStream not supported in this browser.');
            }

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split('\n\n');
                buffer = parts.pop() || ''; // Keep the incomplete part in the buffer

                for (const part of parts) {
                    const lines = part.split('\n');
                    let eventName = 'message';
                    let data = null;

                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            eventName = line.substring(6).trim();
                        } else if (line.startsWith('data:')) {
                            data = line.substring(5).trim();
                        }
                    }

                    if (data) {
                        try {
                            const parsedData = JSON.parse(data);
                            options.onEvent(eventName, parsedData);
                        } catch (e) {
                            console.warn('Failed to parse SSE data', data);
                        }
                    }
                }
            }
        }).catch(error => {
            console.error('Fetch SSE error:', error);
            if (options.onError) options.onError(error);
        });

        // Return the abort function
        return () => controller.abort();
    }

    /**
     * Darkens a hex color by a percentage.
     * @param color The hex color string (e.g., "#RRGGBB").
     * @param percent The percentage to darken (0-100).
     */
    public static darkenColor(color: string, percent: number): string {
        const num = parseInt(color.replace("#", ""), 16),
            amt = Math.round(2.55 * percent),
            R = (num >> 16) - amt,
            G = (num >> 8 & 0x00FF) - amt,
            B = (num & 0x0000FF) - amt;
        return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 + (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    }

    /**
     * Gets a contrasting color (black or white) for a given hex color.
     * @param hexcolor The hex color string.
     */
    public static getContrastColor(hexcolor: string): string {
        if (hexcolor.slice(0, 1) === '#') {
            hexcolor = hexcolor.slice(1);
        }
        if (hexcolor.length === 3) {
            hexcolor = hexcolor.split('').map(char => char + char).join('');
        }
        const r = parseInt(hexcolor.substring(0, 2), 16);
        const g = parseInt(hexcolor.substring(2, 4), 16);
        const b = parseInt(hexcolor.substring(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#000000' : '#FFFFFF';
    }

    static toCurrency(totalDue: any) {
        const value = Number(totalDue) || 0;
        // Format with periods as thousand separators, commas as decimal points, and 'R' as currency symbol
        return 'R ' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /**
     * Cr/Dr marker (incl. trailing space) for a payment row, by its transaction nature. A payment
     * received reduces what the student owes (Credit, "Cr"); a refund/reversal adds it back
     * (Debit, "Dr"). Falls back to the amount sign when the nature is unknown.
     */
    static paymentDrCr(nature: any, amount?: number): string {
        if (nature === 'REFUND_ISSUED') return 'Dr ';
        if (nature === 'PAYMENT_RECEIVED' || nature === 'DEPOSIT_RECEIVED') return 'Cr ';
        return (amount ?? 0) < 0 ? 'Dr ' : 'Cr ';
    }

    /**
     * Cr/Dr marker (incl. trailing space) for an invoice document, by its type. A credit note
     * reduces what the student owes (Credit, "Cr"); every other type (rent, debit note, invoice)
     * is a charge (Debit, "Dr").
     */
    static invoiceDrCr(invoiceType: any): string {
        return invoiceType === 'CREDIT_NOTE' ? 'Cr ' : 'Dr ';
    }

    static dayDiff(date: Date | undefined, date2: Date | undefined) {
        if (!date || !date2) return 0;
        return Math.abs(date2.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    }

    static addDays(startDate: Date | null, duration: number) {
        if (!startDate) return null;
        return new Date(startDate.getTime() + (duration * 24 * 60 * 60 * 1000));

    }

    static generateUUID() {
        // Generate a random UUID (version 4)
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    static openStudentProfile(studentNumber: string, options: { newTab?: boolean } = {}): void {
        const baseUrl = window.location.origin;
        const studentProfileUrl = `${baseUrl}/students/view/${encodeURIComponent(studentNumber)}`;

        if (options.newTab) {
            window.open(studentProfileUrl, '_blank');
        } else {
            window.location.href = studentProfileUrl;
        }
    }

    static convertSpringDate(value: number[]): Date {
        if (!value || value.length < 6) {
            throw new Error('Invalid date value');
        }
        if( value.length === 6) {
            // If only year, month, day, hour, minute, second are provided
            return new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0);
        }
        // If milliseconds are provided, use them as well
        if (value.length < 7) {
            throw new Error('Invalid date value, expected at least 7 elements');
        }
        return new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0, Math.floor((value[6] || 0) / 1000000))
    }

    static dataURLtoFile(dataurl: string, filename: string): File {
        const arr = dataurl.split(',');
        const mime = (arr[0].match(/:(.*?);/) as RegExpMatchArray)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    static getAppVersion() {
        return "1.0.0";
    }

    static updateAppVersionInStorage() {
        localStorage.setItem('app_version', this.getAppVersion());
    }

    static isCacheUpToDate() {
        const version = localStorage.getItem('app_version');
        return version === this.getAppVersion();
    }

    static clearAppStorageIfNeeded() {
        if (!this.isCacheUpToDate()) {
            console.log('App version changed, clearing localStorage cache.');
            localStorage.clear();
            this.updateAppVersionInStorage();
        }
    }

    static getInitials(name: string) {
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    }

    static capitalizeWords(s: string) {
        return s.replace(/\b\w/g, char => char.toUpperCase());
    }
}

interface FetchOptions extends RequestInit {
    parameters?: { [key: string]: any; },
    cacheElement?: HTMLElement,
    cache?: RequestCache,
    type?: string,
    signal?: AbortSignal
}

export enum ToastType {
    SUCCESS = 'success',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info'
}

export interface IAction {
    text: string;        // The text displayed on the button (e.g., 'Add New Student')
    value: string;       // The value the promise resolves with (e.g., 'ADD_STUDENT')
    icon?: string;       // Optional: Material Icons name (e.g., 'person', 'move_to_inbox')
    className?: string;  // Optional: 'green', 'blue darken-1', 'btn-flat', etc.
}

export class SwalUtils {

    static showYearSelect(
        title: string,
        text: string,
        okText: string = 'OK',
        cancelText: string = 'Cancel',
        startYear?: number,
        endYear?: number
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                const currentYear = new Date().getFullYear();
                const finalStart = startYear ?? currentYear - 15;
                const finalEnd = endYear ?? (currentYear + 1);

                const minYear = Math.min(finalStart, finalEnd);
                const maxYear = Math.max(finalStart, finalEnd);

                // Generate array of years in ascending priority
                const years = Array.from(
                    {length: maxYear - minYear + 1},
                    (_, i) => minYear + i
                );

                // Convert array to SweetAlert2 inputOptions format
                const inputOptions = years.reduce((acc: { [key: string]: string }, year) => {
                    acc[year.toString()] = year.toString();
                    return acc;
                }, {});

                Swal.default.fire({
                    title: title,
                    text: text,
                    input: 'select',
                    inputOptions: inputOptions,
                    inputValue: currentYear, // Default to latest year
                    showCancelButton: true,
                    confirmButtonText: okText,
                    cancelButtonText: cancelText,
                }).then((result) => {
                    if (result.isConfirmed && result.value) {
                        resolve(result.value);
                    } else {
                        reject(new Error('Year selection cancelled'));
                    }
                });
            });
        });
    }

    /**
     * The multi-year twin of {@link showYearSelect}, for the places that can legitimately answer
     * for more than one year at a time — an export, say, where staff want this intake and the next
     * one in a single file. The years are a {@link MaterialSelect} in multi-select mode, the app's
     * standard select, rather than a hand-rolled list.
     *
     * <p>Resolves with the chosen years (descending, newest first) and rejects when the dialog is
     * cancelled, matching {@link showYearSelect} so callers can keep using the same `.catch(() => {})`.
     *
     * <p>The `select-swal` classes are what let the dropdown escape the popup — a MaterialSelect
     * mounts its list inside its own container, and SweetAlert clips it out of existence otherwise.
     * See `sass/components/_select-dialog.scss`.
     *
     * @param preSelected years to select on open — defaults to the current year.
     */
    static showYearMultiSelect(
        title: string,
        text: string,
        okText: string = 'OK',
        cancelText: string = 'Cancel',
        preSelected?: number[],
        startYear?: number,
        endYear?: number
    ): Promise<number[]> {
        return new Promise((resolve, reject) => {
            Promise.all([import('sweetalert2'), import('../components/material-select')]).then(([Swal, {MaterialSelect}]) => {
                const currentYear = new Date().getFullYear();
                const finalStart = startYear ?? currentYear - 15;
                const finalEnd = endYear ?? (currentYear + 1);

                const minYear = Math.min(finalStart, finalEnd);
                const maxYear = Math.max(finalStart, finalEnd);

                // Newest first: the years staff actually export are at the top of the list.
                const years = Array.from(
                    {length: maxYear - minYear + 1},
                    (_, i) => maxYear - i
                );

                const selectId = `year-multi-select-${Math.random().toString(36).slice(2, 9)}`;
                const selected = (preSelected?.length ? preSelected : [currentYear]).map(String);

                // `html` wins over `text` in SweetAlert2, so the prompt has to live inside it. The
                // `multiple` attribute is not decoration — MaterialSelect.init reads it off the
                // element to decide multi-select mode, and it overrides the option we pass.
                const html = (text ? `<p style="margin-bottom: 1em;">${text}</p>` : '')
                    + `<div class="year-multi-select" style="text-align: left;">`
                    + `<select id="${selectId}" multiple></select>`
                    + `</div>`;

                let select: import('../components/material-select').MaterialSelect | null = null;

                Swal.default.fire({
                    title: title,
                    html: html,
                    showCancelButton: true,
                    confirmButtonText: okText,
                    cancelButtonText: cancelText,
                    focusConfirm: false,
                    customClass: {popup: 'select-swal', htmlContainer: 'select-swal-body'},
                    didOpen: () => {
                        select = MaterialSelect.init(`#${selectId}`, {
                            items: years.map(year => ({value: String(year), text: String(year)})),
                            multiSelect: true,
                            placeholder: 'Select years',
                            initialSelection: selected
                        });
                    },
                    didClose: () => {
                        // The dialog's DOM goes with it; leaving the instance behind would leave a
                        // zombie in MaterialSelect's registry keyed on an element that no longer exists.
                        select?.destroy();
                        select = null;
                    },
                    preConfirm: () => {
                        const chosen = (select?.selectedIds ?? [])
                            .map(value => parseInt(value))
                            .sort((a, b) => b - a);

                        if (!chosen.length) {
                            Swal.default.showValidationMessage('Please select at least one year');
                            return false;
                        }
                        return chosen;
                    }
                }).then((result) => {
                    if (result.isConfirmed && result.value) {
                        resolve(result.value as number[]);
                    } else {
                        reject(new Error('Year selection cancelled'));
                    }
                });
            });
        });
    }

    /**
     * Shows a SweetAlert2 textarea prompt for a quick free-text input (e.g. a
     * rejection reason). Resolves with the entered text on confirm, or `null`
     * if the user cancels/dismisses. For real multi-field forms use a
     * MaterialModal instead — this is for quick one-off inputs only.
     */
    static showTextPrompt(
        title: string,
        opts: {
            text?: string;
            placeholder?: string;
            /** Pre-filled text, for a prompt that can answer most of itself. Editable as normal. */
            value?: string;
            confirmText?: string;
            cancelText?: string;
            required?: boolean;
            requiredMessage?: string;
        } = {},
    ): Promise<string | null> {
        return new Promise((resolve) => {
            import('sweetalert2').then((Swal) => {
                Swal.default.fire({
                    title,
                    text: opts.text,
                    input: 'textarea',
                    inputValue: opts.value ?? '',
                    inputPlaceholder: opts.placeholder ?? '',
                    showCancelButton: true,
                    confirmButtonText: opts.confirmText ?? 'OK',
                    cancelButtonText: opts.cancelText ?? 'Cancel',
                    inputValidator: (value: string) => {
                        if (opts.required && !value?.trim()) {
                            return opts.requiredMessage ?? 'Please enter a value';
                        }
                        return null;
                    },
                }).then((result: SweetAlertResult) => {
                    resolve(result.isConfirmed ? ((result.value as string) ?? '') : null);
                });
            }).catch(() => resolve(null));
        });
    }

    /**
     * Shows a SweetAlert2 prompt with a single numeric input. Resolves the entered number, or
     * null when cancelled. Enforces an optional min/max and defaults to a required, > 0 value
     * (suitable for quantities).
     */
    static showNumberPrompt(
        title: string,
        opts: {
            text?: string;
            placeholder?: string;
            confirmText?: string;
            cancelText?: string;
            defaultValue?: number;
            min?: number;
            max?: number;
            step?: number;
            invalidMessage?: string;
        } = {},
    ): Promise<number | null> {
        const min = opts.min ?? 1;
        return new Promise((resolve) => {
            import('sweetalert2').then((Swal) => {
                Swal.default.fire({
                    title,
                    text: opts.text,
                    input: 'number',
                    inputValue: opts.defaultValue != null ? String(opts.defaultValue) : '',
                    inputPlaceholder: opts.placeholder ?? '',
                    inputAttributes: {
                        min: String(min),
                        ...(opts.max != null ? { max: String(opts.max) } : {}),
                        step: String(opts.step ?? 1),
                    },
                    showCancelButton: true,
                    confirmButtonText: opts.confirmText ?? 'OK',
                    cancelButtonText: opts.cancelText ?? 'Cancel',
                    inputValidator: (value: string) => {
                        const n = Number(value);
                        if (value === '' || Number.isNaN(n) || n < min || (opts.max != null && n > opts.max)) {
                            return opts.invalidMessage ?? `Please enter a number of at least ${min}`;
                        }
                        return null;
                    },
                }).then((result: SweetAlertResult) => {
                    resolve(result.isConfirmed ? Number(result.value) : null);
                });
            }).catch(() => resolve(null));
        });
    }

    /**
     * Shows a SweetAlert2 prompt with a flatpickr date input (NOT the native browser date input,
     * which renders unreliably inside the themed app). Resolves the picked date as a
     * "MMM dd, yyyy" string (the format the Materialize date (de)serializers expect); rejects with
     * undefined when cancelled so callers can ignore dismissal.
     */
    static showDatePrompt(title: string, text: string, defaultValue?: string): Promise<string> {
        return new Promise((resolve, reject) => {
            Promise.all([import('sweetalert2'), import('flatpickr')]).then(([SwalMod, fpMod]) => {
                const Swal = (SwalMod as any).default || SwalMod;
                const flatpickr = (fpMod as any).default || fpMod;
                const inputId = `date-prompt-${Utils.generateUUID()}`;
                let picked: string | undefined = defaultValue;
                let fp: any;
                Swal.fire({
                    title,
                    // Centre the inline calendar and drop the SweetAlert scrollbar (the inline
                    // flatpickr is slightly taller than the default html-container max-height).
                    customClass: { htmlContainer: 'jc-date-html' },
                    html: `<style>
                            .jc-date-html { overflow: visible !important; max-height: none !important;
                                display: flex; flex-direction: column; align-items: center; }
                            .jc-date-html .flatpickr-calendar.inline { margin: 8px auto 0; box-shadow: none; }
                            /* The app's Materialize base hides native <select>s, which kills flatpickr's
                               month dropdown — force just this one visible so the month picker works. */
                            .jc-date-html select.flatpickr-monthDropdown-months { display: inline-block !important; }
                        </style>
                        <p>${text}</p><input type="text" id="${inputId}" style="display:none">`,
                    didOpen: () => {
                        const host = document.getElementById(inputId) as HTMLInputElement;
                        fp = flatpickr(host, {
                            dateFormat: 'M d, Y',
                            defaultDate: defaultValue,
                            // Render the calendar permanently inline inside the modal — avoids the
                            // open/close, z-index/clipping, and backdrop-dismiss problems of a
                            // popup calendar in a SweetAlert. 'dropdown' month selector (default) gives
                            // a real month picker; the scoped !important style above un-hides its native
                            // <select> that the app's Materialize base would otherwise display:none.
                            inline: true,
                            monthSelectorType: 'dropdown',
                            onChange: (dates: Date[]) => {
                                picked = Utils.dateToString(dates[0]);
                            }
                        });
                    },
                    showCancelButton: true,
                    confirmButtonText: 'Save',
                    cancelButtonText: 'Cancel',
                    // The native month dropdown can extend past the popup; a click on it must not be
                    // read as an outside-click dismissal. Cancel/Escape still close the prompt.
                    allowOutsideClick: false,
                    preConfirm: () => {
                        if (!picked) {
                            Swal.showValidationMessage('Please pick a date');
                            return false;
                        }
                        fp?.destroy();
                        return picked;
                    }
                }).then((result: any) => {
                    if (result.isConfirmed && result.value) resolve(result.value);
                    else reject(undefined); // dismissed — not an error
                }).catch(() => reject(undefined));
            }).catch(err => reject(err));
        });
    }

    /**
     * Shows a SweetAlert2 prompt with an materializecss autocomplete input.
     * @param title
     * @param text
     * @param url
     */
    static showAutocompletePrompt<T>(
        title: string,
        text: string,
        url: string,
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                // Create a unique ID for the autocomplete input
                const autocompleteId = `autocomplete-${Utils.generateUUID()}`;

                // Create HTML content with autocomplete input
                const htmlContent = `
                    <div class="input-field" style="margin-bottom: 128px;">
                        <p>${text}</p>
                        <div class="autocomplete-wrapper">
                            <input type="text" id="${autocompleteId}" class="autocomplete">
                        </div>
                    </div>
                `;

                let value: T | undefined = undefined;
                let autocomplete: MaterialAutocomplete<T> | undefined;
                // Show the SweetAlert with custom HTML
                Swal.default.fire({
                    title: title,
                    html: htmlContent,
                    didOpen: () => {
                        // Get the element
                        const inputElement = document.getElementById(autocompleteId) as HTMLInputElement;
                        inputElement.dataset.url = url; // Store the URL in a data attribute for the autocomplete handler
                        // Initialize the autocomplete
                        autocomplete = new MaterialAutocomplete(inputElement, {
                            onSelect: (selectedValue: T) => {
                                value = selectedValue; // Set the value when an item is selected
                            }
                        })
                    },
                    showCancelButton: true,
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    preConfirm: () => {
                        if (!value) {
                            Swal.default.showValidationMessage('Please select an item');
                            return false;
                        }
                        autocomplete?.destroy();
                        return value;
                    }
                }).then((result) => {
                    if (result.isConfirmed && result.value) {
                        resolve(result.value);
                    } else {
                        reject(new Error('Selection cancelled'));
                    }
                }).catch((error) => {
                    reject(error);
                });
            }).catch(err => {
                reject(new Error('Failed to load SweetAlert2: ' + err));
            });
        });
    }

    /**
     * Shows a SweetAlert2 select dropdown populated from an array of generic objects.
     * @param title The title of the dialog.
     * @param text The descriptive text of the dialog.
     * @param items An array of items of type T to populate the select options.
     * @param valueSelector A function that takes an item of type T and returns its string value (used as the option key and resolved value).
     * @param displaySelector A function that takes an item of type T and returns its string display text (used as the option label).
     * @param okText Text for the confirm button. Defaults to 'OK'.
     * @param cancelText Text for the cancel button. Defaults to 'Cancel'.
     * @param defaultValue An optional default value (must match a value returned by valueSelector). If not provided, the first item will be selected.
     * @returns A Promise that resolves with the selected value string, or rejects if cancelled.
     */
    static showObjectSelect<T>(
        title: string,
        text: string,
        items: T[],
        valueSelector: (item: T) => string,
        displaySelector: (item: T) => string,
        okText: string = 'OK',
        cancelText: string = 'Cancel',
        defaultValue?: string
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((SwalModule) => { // SwalModule might be { default: Swal } or just Swal
                const Swal = SwalModule.default || SwalModule;

                if (!items || items.length === 0) {
                    reject(new Error('No items provided for selection.'));
                    return;
                }

                // Generate inputOptions from items using the provided selector functions
                const inputOptions = items.reduce((acc: { [key: string]: string }, item) => {
                    const value = valueSelector(item);
                    const display = displaySelector(item);
                    acc[value] = display;
                    return acc;
                }, {});

                let resolvedInputValue: string | undefined = undefined;
                if (defaultValue !== undefined) {
                    // If a defaultValue is provided, check if it's a valid key in our options
                    if (Object.prototype.hasOwnProperty.call(inputOptions, defaultValue)) {
                        resolvedInputValue = defaultValue;
                    } else {
                        console.warn(`Provided defaultValue '${defaultValue}' is not a valid option value. SweetAlert2 may not pre-select it or may default to the first option.`);
                        // Still pass it to Swal, it might handle it or developer can see the warning
                        resolvedInputValue = defaultValue;
                    }
                } else {
                    // If no defaultValue is provided, default to the value of the first item in the list
                    resolvedInputValue = valueSelector(items[0]);
                }

                Swal.fire({
                    title: title,
                    text: text,
                    input: 'select',
                    inputOptions: inputOptions,
                    inputValue: resolvedInputValue,
                    showCancelButton: true,
                    confirmButtonText: okText,
                    cancelButtonText: cancelText,
                    // You can add other SweetAlert2 options here if needed
                } as SweetAlertOptions).then((result: SweetAlertResult) => {
                    if (result.isConfirmed && result.value) {
                        // result.value is the key from inputOptions, which is what valueSelector returned
                        resolve(result.value as string);
                    } else {
                        reject(new Error('Selection cancelled or no value selected.'));
                    }
                });
            }).catch(err => {
                // Catch errors from dynamic import itself
                reject(new Error('Failed to load SweetAlert2: ' + err));
            });
        });
    }

    static showLoading(title: string, text: string): void {
        import('sweetalert2').then((Swal) => {
            Swal.default.fire({
                title: title,
                html: text,
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                loaderHtml: '<i class="material-icons spinner">hourglass_empty</i>'
            });
        });
    }

    static showError(title: string, text: string): void {
        import('sweetalert2').then((Swal) => {
            Swal.default.fire({
                title: title,
                text: text,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        });
    }

    static showSuccess(title: string, text: string): void {
        import('sweetalert2').then((Swal) => {
            Swal.default.fire({
                title: title,
                text: text,
                icon: 'success',
                confirmButtonText: 'OK'
            });
        });
    }

    static showWarning(title: string, text: string): void {
        import('sweetalert2').then((Swal) => {
            Swal.default.fire({
                title: title,
                text: text,
                icon: 'warning',
                confirmButtonText: 'OK'
            });
        });
    }

    /**
     * Shows a SweetAlert2 dialog with multiple custom action buttons
     * using the .action-item style.
     * @param title The title of the dialog.
     * @param text The descriptive text of the dialog.
     * @param actions An array of IAction objects to create buttons for.
     * @param cancelText Text for the cancel button.
     * @returns A Promise that resolves with the 'value' of the clicked action, or rejects if cancelled.
     */
    static showActions(
        title: string,
        text: string,
        actions: IAction[],
        cancelText: string = 'Cancel'
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                // 1. Generate the HTML for our custom .action-item buttons
                const buttonsHtml = actions.map(action => {
                    // Conditionally add the icon if one is provided
                    const iconHtml = action.icon
                        ? `<i class="material-icons left">${action.icon}</i>`
                        : '';

                    return `
                        <button
                            type="button"
                            class="swal-action-btn action-item ${action.className || ''}"
                            data-value="${action.value}"
                        >
                            ${iconHtml}
                            ${action.text}
                        </button>
                    `;
                }).join('');
                const htmlContent = `
                    <p>${text}</p>
                    <div
                        class="action-menu" 
                        style="margin-top: 20px; display: flex; flex-direction: column; gap: 10px;">
                        ${buttonsHtml}
                    </div>
                `;

                // 3. Fire the SweetAlert
                Swal.default.fire({
                    title: title,
                    html: htmlContent,
                    showConfirmButton: false, // We hide the default confirm button
                    showCancelButton: true,
                    cancelButtonText: cancelText,
                    didOpen: (modalElement) => {
                        // 4. Add click listeners to our custom buttons
                        modalElement.querySelectorAll('.swal-action-btn').forEach(button => {
                            button.addEventListener('click', () => {
                                const value = (button as HTMLElement).dataset.value;
                                if (value) {
                                    // 5. Resolve the promise with the button's value
                                    resolve(value);
                                    Swal.default.close();
                                }
                            });
                        });
                    }
                }).then((result) => {
                    // 6. If the modal is dismissed (Cancel, overlay, Esc), reject the promise
                    if (result.isDismissed) {
                        reject(new Error('Actions cancelled by user'));
                    }
                });

            }).catch(err => {
                reject(new Error('Failed to load SweetAlert2: ' + err));
            });
        });
    }

    static showAction(text: string, okText = 'Ok', cancelText = 'Cancel'): Promise<SweetAlertResult> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                Swal.default.fire({
                    title: 'Are you sure?',
                    text: text,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: okText,
                    cancelButtonText: cancelText
                }).then((result) => {
                    if (result.isConfirmed) {
                        resolve(result);
                    } else {
                        reject(result);
                    }
                }).catch((error) => {
                    reject(error);
                });
            });
        });
    }

    static showPrompt(title: string, text: string, {inputType, okText, cancelText, defaultValue = ''} = {
        inputType: "text",
        okText: 'Ok',
        cancelText: 'Cancel'
    }): Promise<SweetAlertResult> {
        if (okText === undefined) okText = 'Ok';
        if (cancelText === undefined) cancelText = 'Cancel';
        if (inputType === undefined) inputType = 'text';

        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                Swal.default.fire({
                    title: title,
                    text: text,
                    input: inputType as SweetAlertInput,
                    inputValue: defaultValue,
                    showCancelButton: true,
                    confirmButtonText: okText,
                    cancelButtonText: cancelText,
                }).then((result) => {
                    if (result.isConfirmed && result.value) {
                        resolve(result);
                    } else {
                        reject();
                    }
                }).catch((error) => {
                    reject();
                });
            });
        });
    }

    static showHtml(title: string, content: string, icon: string = 'info'): Promise<SweetAlertResult> {
        return new Promise((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                Swal.default.fire({
                    title: title,
                    html: content,
                    iconHtml: '<i class="material-icons">' + icon + '</i>',
                    showConfirmButton: false,
                    showCancelButton: false,
                }).then((result) => {
                    resolve(result);
                }).catch((error) => {
                    reject(error);
                });
            });
        });
    }

    static showInfo(title: string, text: string): void {
        import('sweetalert2').then((Swal) => {
            Swal.default.fire({
                title: title,
                text: text,
                icon: 'info',
                confirmButtonText: 'OK'
            });
        });
    }

    static close(opt?: SweetAlertResult) {
        import('sweetalert2').then((Swal) => {
            Swal.default.close(opt);
        });
    }

    static showBuildingSelect(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            import('sweetalert2').then((Swal) => {
                BuildingApi.Instance.getUserBuildings().then((buildings: BuildingData[]) => {
                    Swal.default.fire({
                        title: 'Select a building',
                        text: 'Please select a building from the list',
                        input: 'select',
                        inputOptions: buildings.map(value => value.name),
                        inputValue: buildings[0].name, // Default to latest year
                        showCancelButton: true,
                        confirmButtonText: 'Select',
                        cancelButtonText: 'Cancel',
                    }).then((result) => {
                        if (result.isConfirmed && result.value) {
                            resolve(buildings[result.value].name);
                        } else {
                            reject(new Error('Year selection cancelled'));
                        }
                    });
                }).catch(ex => {
                    Utils.showErrorToast('Failed to load buildings', ex);
                    reject(ex);
                })
            });
        });
    }

    /**
     * Opens a Materialize CSS datepicker to select a date.
     * The function returns a promise that resolves with the selected Date object, or undefined if the user cancels.
     * @param title The title to use for the datepicker (used as label).
     * @param description A descriptive text (shown as placeholder).
     * @returns A Promise<Date | undefined> which resolves with the user's selected date or undefined.
     */
    static async pickADate(title: string, description: string): Promise<Date | undefined> {
        // Create a temporary input element for the datepicker
        const datepickerInput = document.createElement('input');
        datepickerInput.type = 'text';
        datepickerInput.className = 'datepicker';
        datepickerInput.style.position = 'absolute';
        datepickerInput.style.left = '-9999px'; // Hide the input element off-screen
        datepickerInput.placeholder = description;

        // Add the input to the DOM temporarily
        document.body.appendChild(datepickerInput);

        return new Promise((resolve) => {
            let selectedDate: Date | undefined = undefined;

            // Initialize the Materialize datepicker
            const datepickerInstance = M.Datepicker.init(datepickerInput, {
                format: 'yyyy-mm-dd',
                autoClose: true,
                container: document.body,
                setDefaultDate: false,
                defaultDate: new Date(),
                onSelect: (newDate: Date) => {
                    selectedDate = newDate;
                    // Auto-close and resolve when a date is selected
                    setTimeout(() => {
                        datepickerInstance.destroy();
                        datepickerInput.remove();
                        resolve(selectedDate);
                    }, 100);
                },
                onClose: () => {
                    // Clean up and resolve when the picker is closed without selection
                    datepickerInstance.destroy();
                    datepickerInput.remove();
                    resolve(selectedDate);
                }
            });

            // Open the datepicker immediately
            datepickerInstance.open();
        });
    }
}

export interface PostOptions {
    body?: any,
    parameters?: { [key: string]: any; },
    type?: string,
    headers?: Headers,
    disableCache?: boolean
}

export class CommonFunctions {
    static sendLeaseLink(studentNumber: string, text?: string) {
        return new Promise((resolve, reject) => {
            SwalUtils.showAction(text || 'Are you sure you want to send the lease agreement link to the student').then((result) => {
                SwalUtils.showLoading('Sending lease agreement link', 'Please wait...');

                Utils.post('/requests/resend/lease', {body: {content: [studentNumber]}}).then(function (r) {
                    Utils.showToastMessage('Successfully sent the lease agreement link, it will expire within 5 days.', ToastType.SUCCESS);
                    resolve(r);
                    SwalUtils.close();
                }).catch(function (r) {
                    Utils.showErrorToast('Could not send link', r);
                    reject(r);
                    SwalUtils.close();
                });
            });
        });
    }

    static openLeaseModal(studentNumber: string, {building, studentRoom, leaseAgreementId} = {
        building: undefined,
        studentRoom: undefined,
        leaseAgreementId: undefined
    }) {
        if (studentRoom === undefined || leaseAgreementId === undefined) {
            return Promise.reject('Invalid parameters');
        }

        const modal = document.querySelector<HTMLDivElement>('#sign-lease-agreement-modal');

        if (!modal) return Promise.reject('Could not find modal');

        const signModal = new SignLeaseModal(modal, {
            studentNumber: studentNumber,
            studentRoom: studentRoom,
            studentLeaseAgreementId: leaseAgreementId,
            building: building
        });

        return signModal.open();
    }
}


export namespace Jez {

    export async function updateStudentNumber(studentNumber: string) {
        try {
            const result = await SwalUtils.showPrompt('Change Student Number', 'Please enter the new student number');
            const response = await Utils.post('/students/update/' + studentNumber, {
                parameters: {studentNumber: result.value},
            });
            return result.value;
            Utils.showToastMessage('Student Number Updated', ToastType.SUCCESS);
        } catch (e) {
            Utils.showErrorToast('Failed to update student number', e);
        }
    }

    /**
     * Signs the lease agreement for a student.
     *
     * @param {string} buildingName - The name of the building.
     * @param {string} studentNumber - The student's number.
     * @param {string} studentRoomId - The ID of the student's room.
     * @param {string} leaseId - The ID of the lease agreement.
     * @returns {Promise<void>} - A promise that resolves when the lease agreement is signed.
     */
    export async function signLeaseAgreement(buildingName: string, studentNumber: string, studentRoomId: string, leaseId: string, sendEmail = true): Promise<void> {
        const leaseSignatureModal = SignatureModal.Init('lease-signature', '/api/accommodation/buildings/signatures/' + buildingName);
        const signature = await leaseSignatureModal.sign;
        let url = '/api/leases/manager/sign/' + studentNumber;

        let data = {
            signature: signature,
            studentRoom: studentRoomId,
            leaseAgreementId: leaseId,
            sendEmail: sendEmail
        }

        SwalUtils.showLoading('Sending Lease', 'Please wait while we send out the signed lease agreement');
        return Utils.post(url, {
            body: data,
        }).then((r) => {
            Utils.showToastMessage('Lease agreement signed successfully');
            SwalUtils.close();
        }).catch((r) => {
            if (r && r.responseText) {
                if (r.responseText.includes('Failed to send email.')) {
                    Utils.showToastMessage('Successfully signed lease agreement, but a copy failed to be sent to the student. Please manually send the lease agreement to the student.', ToastType.WARNING, 8000);
                }
            } else {
                Utils.showErrorToast('Failed to sign lease agreement', r);
            }
            SwalUtils.close();
        });
    }

}